import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  AlertTitle,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIconButton from "components/common/DeleteOutlineIconButton";
import { useState } from "react";
import theme from "lib/theme";
import { formatDate } from "utils/helpers";
import { WorkOrder, WorkOrderTask } from "types/workOrder";
import {
  formatWorkOrderAsset,
  formatWorkOrderEmployee,
  formatWorkOrderLocation,
  formatWorkOrderTaskShipTo,
  workOrderTaskActionLabel,
} from "./workOrderDisplay";
import { Link as RouterLink } from "react-router-dom";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { surfacePanelSx, portalDataGridSx } from "constants/pageSurfaces";

const inputWhiteSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
  },
};

const PORTAL_TICKETS_PAGE_SIZE = 25;

/** Default grid row height with summary + “View details” (compact vs. full expand dialog). */
const WORK_ORDER_GRID_ROW_HEIGHT_PX = 104;

const STATUS_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  "Under Review": { bg: "#e3f2fd", text: "#1565c0", border: "#90caf9" },
  "In Transit": { bg: "#fff3e0", text: "#e65100", border: "#ffb74d" },
  Complete: { bg: "#e8f5e9", text: "#2e7d32", border: "#81c784" },
  "Under Assessment": { bg: "#f3e5f5", text: "#7b1fa2", border: "#ba68c8" },
  "End of Life": { bg: "#eceff1", text: "#546e7a", border: "#90a4ae" },
  "Under Repair": { bg: "#fff8e1", text: "#f57f17", border: "#ffca28" },
};

function getStatusStyle(status: string | undefined) {
  if (!status) return { bg: "#f5f5f5", text: "#616161", border: "#e0e0e0" };
  return STATUS_STYLES[status] ?? { bg: "#f5f5f5", text: "#616161", border: "#e0e0e0" };
}

function sortWorkOrderTasks(wo: WorkOrder): WorkOrderTask[] {
  return [...(wo.tasks ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

function lineItemsGridSummary(wo: WorkOrder): { headline: string; sub?: string; showDetailsButton: boolean } {
  const tasks = sortWorkOrderTasks(wo);
  if (tasks.length === 0) {
    const legacyParts = [
      formatWorkOrderEmployee(wo.employee),
      formatWorkOrderAsset(wo.asset),
      formatWorkOrderLocation(wo.location),
    ].filter((s) => s && s !== "—");
    const headline = legacyParts.length ? legacyParts.join(" · ") : "—";
    const clipped = headline.length > 140 ? `${headline.slice(0, 137)}…` : headline;
    return { headline: clipped, showDetailsButton: legacyParts.length > 0 };
  }
  const labels = tasks.map((t) => workOrderTaskActionLabel(t));
  const distinct = Array.from(new Set(labels));
  let actionPart: string;
  if (distinct.length <= 2) {
    actionPart = distinct.join(", ");
  } else {
    actionPart = `${distinct.slice(0, 2).join(", ")} +${distinct.length - 2} more`;
  }
  const headline = `${tasks.length} line item${tasks.length === 1 ? "" : "s"} · ${actionPart}`;
  const first = tasks[0];
  const subParts = [formatWorkOrderEmployee(first.employee), formatWorkOrderAsset(first.asset)].filter((s) => s && s !== "—");
  const sub =
    tasks.length === 1
      ? subParts.join(" · ") || undefined
      : subParts.length
        ? `${subParts.join(" · ")} · …`
        : undefined;
  return { headline, sub, showDetailsButton: true };
}

interface WorkOrderCardsProps {
  workOrders: WorkOrder[];
  isLoading: boolean;
  errorMessage: string | null;
  deleteWorkOrder: (uuid: string) => Promise<void>;
  meta: { total_pages: number; count: number };
  page: number;
  onPageChange: (page: number) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
}

export default function WorkOrderCards({
  workOrders,
  isLoading,
  errorMessage,
  deleteWorkOrder,
  meta,
  page,
  onPageChange,
  searchQuery,
  onSearchChange,
}: WorkOrderCardsProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workOrderToDelete, setWorkOrderToDelete] = useState<WorkOrder | null>(null);
  const [lineItemsDialogWo, setLineItemsDialogWo] = useState<WorkOrder | null>(null);

  const handleDeleteClick = (wo: WorkOrder) => {
    setWorkOrderToDelete(wo);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (workOrderToDelete?.uuid) {
      deleteWorkOrder(workOrderToDelete.uuid).then(() => {
        setDeleteDialogOpen(false);
        setWorkOrderToDelete(null);
      });
    }
  };

  const truncate = (str: string | undefined, max: number) => {
    if (!str) return "—";
    return str.length > max ? str.slice(0, max) + "…" : str;
  };

  const renderLineItems = (wo: WorkOrder) => {
    const tasks = sortWorkOrderTasks(wo);
    if (tasks.length === 0) {
      const legacyParts = [
        formatWorkOrderEmployee(wo.employee),
        formatWorkOrderAsset(wo.asset),
        formatWorkOrderLocation(wo.location),
      ].filter((s) => s && s !== "—");
      return (
        <Typography variant="body2" color="text.secondary">
          {legacyParts.length ? legacyParts.join(" · ") : "—"}
        </Typography>
      );
    }
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, width: "100%", maxWidth: "100%", py: 0.5 }}>
        {tasks.map((t, idx) => (
          <Box
            key={t.uuid ?? t.id ?? idx}
            sx={{
              borderLeft: 2,
              borderColor: "primary.light",
              pl: 1.25,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.25 }}>
              <Typography variant="body2" fontWeight={600} component="span">
                {workOrderTaskActionLabel(t)}
              </Typography>
              {t.status ? (
                <Chip size="small" label={t.status} variant="outlined" sx={{ height: 22, fontSize: "0.7rem" }} />
              ) : null}
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.45 }}>
              <strong>Employee:</strong> {formatWorkOrderEmployee(t.employee)}
              {" · "}
              <strong>Asset:</strong> {formatWorkOrderAsset(t.asset)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.45 }}>
              <strong>Ship to:</strong> {formatWorkOrderTaskShipTo(t)}
            </Typography>
            {t.description ? (
              <Typography variant="caption" display="block" sx={{ mt: 0.25, color: "text.primary" }}>
                {truncate(t.description, 140)}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "orderNumber",
      headerName: "Order number",
      minWidth: 130,
      flex: 0.5,
      valueGetter: (p) => (p.row as WorkOrder).orderNumber ?? (p.row as WorkOrder).order_number ?? "—",
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 160,
      flex: 0.6,
      sortable: false,
      renderCell: (params) => {
        const wo = params.row as WorkOrder;
        const summary = wo.taskStatusSummary ?? wo.task_status_summary;
        const headerStatus = wo.status ?? "";
        const chipLabel = summary || headerStatus || "—";
        const style = summary
          ? { bg: "#f5f5f5", text: "#616161", border: "#e0e0e0" }
          : getStatusStyle(headerStatus);
        return (
          <Chip
            size="small"
            label={chipLabel.length > 48 ? `${chipLabel.slice(0, 48)}…` : chipLabel}
            sx={{
              backgroundColor: style.bg,
              color: style.text,
              border: `1px solid ${style.border}`,
              fontWeight: 500,
              maxWidth: 360,
            }}
          />
        );
      },
    },
    {
      field: "lineItems",
      headerName: "Line items",
      minWidth: 300,
      flex: 1.2,
      sortable: false,
      renderCell: (params) => {
        const wo = params.row as WorkOrder;
        const { headline, sub, showDetailsButton } = lineItemsGridSummary(wo);
        return (
          <Box sx={{ py: 0.25, width: "100%", pr: 0.5 }}>
            <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.35 }}>
              {headline}
            </Typography>
            {sub ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.4 }}>
                {sub}
              </Typography>
            ) : null}
            {showDetailsButton ? (
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setLineItemsDialogWo(wo);
                }}
                sx={{ mt: 0.5, p: 0, minWidth: "auto", textTransform: "none" }}
              >
                View details
              </Button>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: "created",
      headerName: "Created",
      minWidth: 120,
      flex: 0.45,
      sortable: false,
      valueGetter: (p) => {
        const wo = p.row as WorkOrder;
        const d = wo.createdAt ?? wo.created_at;
        return d ? formatDate(d) : "—";
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 100,
      align: "right",
      headerAlign: "right",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const wo = params.row as WorkOrder;
        return (
          <>
            <IconButton size="small" component={RouterLink} to={`/work_orders/${wo.uuid}/edit`} aria-label="Edit">
              <EditIcon fontSize="small" />
            </IconButton>
            <DeleteOutlineIconButton onClick={() => handleDeleteClick(wo)} aria-label="Delete work order" />
          </>
        );
      },
    },
  ];

  if (errorMessage) {
    return (
      <Box sx={{ mt: 6, border: "1px solid", borderColor: theme.palette.divider, background: "white", minHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {errorMessage}
        </Alert>
      </Box>
    );
  }

  const showOnboardingEmpty =
    !isLoading && meta.count === 0 && !searchQuery.trim();

  if (showOnboardingEmpty) {
    return (
      <Box sx={{ mt: 6, p: 4, borderRadius: "12px", boxShadow: 2, background: "white", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mt: 2 }}>
          No work orders yet
        </Typography>
        <Typography mt={1} maxWidth="400px" textAlign="center" sx={{ color: "#555" }}>
          Submit your first work order using the button below.
        </Typography>
        <Button component={RouterLink} variant="contained" size="large" to="/work_orders/new" sx={{ mt: 4 }}>
          New Work Order
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search work orders..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ minWidth: 220, ...inputWhiteSx }}
        />
      </Box>
      <Paper variant="outlined" sx={{ ...surfacePanelSx, width: "100%", overflow: "hidden" }}>
        <DataGridPro
          autoHeight
          disableColumnMenu
          loading={isLoading}
          rows={workOrders}
          columns={columns}
          getRowId={(row) => row.uuid ?? String(row.id)}
          rowHeight={WORK_ORDER_GRID_ROW_HEIGHT_PX}
          sx={portalDataGridSx}
          initialState={{ pinnedColumns: { right: ["actions"] } }}
          pagination
          paginationMode="server"
          rowCount={meta.count}
          page={page - 1}
          pageSize={PORTAL_TICKETS_PAGE_SIZE}
          rowsPerPageOptions={[PORTAL_TICKETS_PAGE_SIZE]}
          onPageChange={(p) => onPageChange(p + 1)}
          hideFooter={meta.total_pages <= 1}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ py: 4, px: 2, textAlign: "center", color: "text.secondary" }}>
                {searchQuery.trim() ? "No work orders match your search." : "No work orders on this page."}
              </Box>
            ),
          }}
        />
      </Paper>
      <Dialog
        open={!!lineItemsDialogWo}
        onClose={() => setLineItemsDialogWo(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          Line items — {lineItemsDialogWo?.orderNumber ?? lineItemsDialogWo?.order_number ?? "Work order"}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "70vh", overflowY: "auto" }}>
          {lineItemsDialogWo ? renderLineItems(lineItemsDialogWo) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLineItemsDialogWo(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete work order?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete work order {workOrderToDelete?.orderNumber ?? workOrderToDelete?.order_number}? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
